import { hot } from "react-hot-loader";

import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { ApolloProvider } from "@apollo/react-hooks";
import { arrayOf, bool, number, shape, string } from "prop-types";

import { cmsPlacementsPropType } from "src/features/content-management-system/constants/cmsPropTypes";
import apolloClient from "src/shared/utilities/graphql/apolloClient";

import { agentMatchItemPropType } from "../../propTypes";
import * as propTypes from "../../propTypes";
import store from "../../redux/store";
import AmenitiesSection from "../AmenitiesSection";
import FairHousingBanner from "../FairHousingBanner";
import HeroBlock from "../HeroBlock";
import HomePageCMSBanner from "../HomePageCMSBanner";
import IndustryProfessional from "../IndustryProfessional";
import ListingAgentMatchBanner from "../ListingAgentMatchBanner";
import MobileApps from "../MobileApps";
import RecentlyViewedListings from "../RecentlyViewedListings";
import RecentSearches from "../RecentSearches";
import RecommendedListings from "../RecommendedListings";
import RecommendedResources from "../RecommendedResources";
import RentBuySellSection from "../RentBuySellSection";
import Resources from "../Resources";
import SellerBanner from "../SellerBanner";
import SignInRegisterSection from "../SignInRegisterSection";
import SocialSection from "../SocialSection";

const HomePageRedesign = ({
  abTests,
  areaOptions,
  cmsPlacements,
  helpfulBlogPosts,
  lastSearchType,
  latestHomeClaimForUser,
  recentSearches,
  recentlyViewedListings,
  recommendedListings,
  searchRentals,
  searchSales,
  user,
  userRole,
  userSegments
}) => {
  const priceWidgetTest = abTests.find(
    ({ experiment }) => experiment === "gold_price_widget"
  );
  const isResourcesVisible = !user || (recentSearches.length === 0 && user);
  const isRecommendedResourcesVisible = user && recentSearches.length > 0;
  const isSellerBannerVisible =
    ["sell", "buy_sell"].includes(user?.profile?.searchType) &&
    !latestHomeClaimForUser?.id;
  const isListingAgentMatchBannerVisible =
    user &&
    user.listingAgentMatches?.length > 0 &&
    user.listingAgentMatches[0].homesSoldSimilar > 0 &&
    latestHomeClaimForUser?.id;

  useEffect(() => {
    if (priceWidgetTest?.fireable && window?.dataLayer) {
      window.dataLayer.push({
        event: "split_experiment_start",
        event_category: "split_experiment",
        event_action: "start",
        event_label: `gold_price_widget: ${priceWidgetTest.variant}`
      });
    }
  }, []);

  return (
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <HeroBlock
          areaOptions={areaOptions}
          lastSearchType={lastSearchType}
          priceWidgetTest={priceWidgetTest?.variant || "no_widget"}
          userProfile={user?.profile}
          userRole={userRole}
        />
        <FairHousingBanner />
        {recentSearches.length > 0 && (
          <RecentSearches
            recentSearches={recentSearches}
            user={user}
            userRole={userRole}
          />
        )}
        {isSellerBannerVisible && (
          <SellerBanner isRecentSearches={recentSearches.length > 0} />
        )}
        {isListingAgentMatchBannerVisible && (
          <ListingAgentMatchBanner
            listingAgentMatches={user.listingAgentMatches}
            propertyId={latestHomeClaimForUser.property.id}
            userId={user.id}
          />
        )}
        {recommendedListings && recommendedListings.length > 0 && (
          <RecommendedListings recommendedListings={recommendedListings} />
        )}
        {recentlyViewedListings && recentlyViewedListings.length > 0 && (
          <RecentlyViewedListings
            isRecommendedListings={
              recommendedListings && recommendedListings.length > 0
            }
            recentlyViewedListings={recentlyViewedListings}
          />
        )}
        {isRecommendedResourcesVisible && (
          <RecommendedResources helpfulBlogPosts={helpfulBlogPosts} />
        )}
        {user && recentSearches.length > 0 && (
          <HomePageCMSBanner
            cmsPlacements={cmsPlacements}
            userSegments={userSegments}
          />
        )}
        <RentBuySellSection />
        {(!user || user?.id === 0 || recentSearches.length === 0) && (
          <HomePageCMSBanner
            cmsPlacements={cmsPlacements}
            userSegments={userSegments}
          />
        )}
        {!user && <SignInRegisterSection />}
        <AmenitiesSection
          rentalsCount={searchRentals?.totalCount}
          salesCount={searchSales?.totalCount}
        />
        {isResourcesVisible && <Resources />}
        <MobileApps />
        {user === null && <IndustryProfessional />}
        <SocialSection />
      </Provider>
    </ApolloProvider>
  );
};

HomePageRedesign.defaultProps = {
  abTests: [],
  cmsPlacements: [],
  helpfulBlogPosts: [],
  lastSearchType: "rentals",
  latestHomeClaimForUser: null,
  listingAgentMatches: [],
  recentlyViewedListings: [],
  recentSearches: [],
  recommendedListings: [],
  user: null,
  userRole: null
};

HomePageRedesign.propTypes = {
  abTests: arrayOf(
    shape({
      experiment: string,
      fireable: bool,
      variant: string
    })
  ),
  areaOptions: propTypes.locations.isRequired,
  cmsPlacements: cmsPlacementsPropType,
  helpfulBlogPosts: arrayOf(
    shape({
      imageUrl: string.isRequired,
      shortHeadline: string.isRequired,
      title: string.isRequired,
      url: string.isRequired
    })
  ),
  lastSearchType: string,
  latestHomeClaimForUser: shape({
    id: number,
    property: shape({
      id: number
    })
  }),
  listingAgentMatches: arrayOf(agentMatchItemPropType),
  recentSearches: propTypes.recentSearchesProps,
  recentlyViewedListings: propTypes.recentlyViewedListingsProps,
  recommendedListings: propTypes.recommendedListingsPropType,
  searchRentals: shape({
    totalCount: number
  }).isRequired,
  searchSales: shape({
    totalCount: number
  }).isRequired,
  user: propTypes.user,
  userRole: string,
  userSegments: arrayOf(string).isRequired
};

export default hot(module)(HomePageRedesign);
